import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { fetchAllRows } from '../../lib/fetchAllRows'
import SearchableSelect from '../SearchableSelect'
import CreateCustomerDialog from '../CreateCustomerDialog'
import JobPartyAddress from './JobPartyAddress'

// The job's Customer, shown in the job header (under "Import · SEA · SINGLE") so it is visible on every tab.
// NAMING: this is the column jobs.invoice_to_id. Staff-facing name is ALWAYS "Customer" (never "Invoice To") -- it is one
// thing: the party the shipment belongs to and who is billed. Do not add a second column for it; keep the column name as is.
// PLACEMENT RULE: a party that follows a bill of lading (consignor, importer, notify, overseas agent) lives inside the
// HBL/MBL tabs; a party that follows the shipment (this one -- exactly one per job) lives here, outside the tabs.
interface Props {
  jobData: any
  setJobData: (data: any) => void
  readOnly?: boolean
  userEmail: string | null
}

interface Opt { id: string; label: string; code: string; name: string }

export default function JobCustomerLine({ jobData, setJobData, readOnly, userEmail }: Props) {
  const customerId: string = jobData.invoice_to_id || ''
  const [current, setCurrent] = useState<{ code: string; name: string } | null>(null)
  const [options, setOptions] = useState<Opt[] | null>(null)
  const [changing, setChanging] = useState(false)
  const [createName, setCreateName] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!customerId) { setCurrent(null); return }
    supabase.from('customers').select('code, name').eq('id', customerId).maybeSingle()
      .then(({ data }) => { if (!cancelled) setCurrent(data ? { code: data.code, name: data.name } : null) })
    return () => { cancelled = true }
  }, [customerId])

  const startChange = async () => {
    setChanging(true)
    if (options) return
    try {
      const rows = await fetchAllRows<{ id: string; code: string; name: string }>((from, to) =>
        supabase.from('customers').select('id, code, name').eq('is_active', true).order('name').order('id').range(from, to))
      setOptions(rows.map(c => ({ id: c.id, code: c.code, name: c.name, label: `${c.code || '⚠ NO CODE'} - ${c.name}` })))
    } catch (e) {
      console.error('Error loading customers:', e)
      setChanging(false)
    }
  }

  const pick = (id: string) => {
    setJobData({ ...jobData, invoice_to_id: id })
    setChanging(false)
  }

  return (
    <div className="mt-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-semibold text-gray-700 dark:text-gray-300">Customer:</span>
        {changing ? (
          <div className="w-96 max-w-full">
            <SearchableSelect
              options={options ?? []}
              codeKey="code" nameKey="name"
              value={customerId}
              onChange={pick}
              placeholder="Search customer..."
              onCreateClick={(name) => setCreateName(name)}
            />
          </div>
        ) : customerId ? (
          <Link to={`/customers/${customerId}`} className="text-blue-600 dark:text-blue-400 hover:underline">
            {current ? `${current.code || '⚠ NO CODE'} - ${current.name}` : '…'}
          </Link>
        ) : (
          <span className="text-amber-700 dark:text-amber-400">not set</span>
        )}
        {!readOnly && (changing
          ? <button type="button" onClick={() => setChanging(false)} className="text-xs text-gray-500 hover:underline">Cancel</button>
          : <button type="button" onClick={startChange} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">{customerId ? 'Change company' : 'Set customer'}</button>)}
      </div>
      {customerId && jobData.id && !changing && (
        <JobPartyAddress jobId={jobData.id} partyRole="invoice_to" customerId={customerId} userEmail={userEmail} readOnly={readOnly}
          refreshKey={String(jobData.updated_at ?? '')} />
      )}
      <CreateCustomerDialog
        isOpen={createName !== null}
        initialName={createName ?? ''}
        fieldType="other"
        onClose={() => setCreateName(null)}
        onSuccess={(c) => {
          setOptions(prev => prev ? [...prev, { id: c.id, code: c.code, name: c.name, label: `${c.code || '⚠ NO CODE'} - ${c.name}` }] : prev)
          setCreateName(null)
          pick(c.id)
        }}
      />
    </div>
  )
}
